import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Pencil, Trash2, Search, Save, X } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import { useAuth } from "../../lib/auth/hooks";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import {
  fetchEnumGroup, updateEnumGroup,
  createEnumGroupValue, updateEnumGroupValue, deleteEnumGroupValue,
} from "../../lib/api/products";
import type { EnumGroup, EnumGroupValue } from "../../lib/api/products";
import * as pageStyles from "../organizations/OrganizationDetailPage.css";

function severityBadge(severity?: string | null) {
  if (!severity) return null;
  const styles: Record<string, { bg: string; color: string }> = {
    critical: { bg: "#7F1D1D", color: "#fff" },
    error:    { bg: "#FEE2E2", color: "#DC2626" },
    warning:  { bg: "#FEF3C7", color: "#D97706" },
    info:     { bg: "#EFF6FF", color: "#2563EB" },
  };
  const s = styles[severity] ?? styles.info;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 9999,
      background: s.bg, color: s.color, textTransform: "capitalize" as const }}>
      {severity}
    </span>
  );
}

interface ValueFormState {
  key: string;
  label: string;
  description: string;
  meta: string;
}

const emptyValueForm = (): ValueFormState => ({ key: "", label: "", description: "", meta: "" });

export function EnumGroupDetailPage({ groupId }: { groupId: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { confirm, ConfirmModal } = useConfirmModal();

  const [group, setGroup] = React.useState<EnumGroup | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Inline edit state for name/description
  const [editingName, setEditingName] = React.useState(false);
  const [nameValue, setNameValue] = React.useState("");
  const [editingDesc, setEditingDesc] = React.useState(false);
  const [descValue, setDescValue] = React.useState("");
  const [savingMeta, setSavingMeta] = React.useState(false);
  const [metaError, setMetaError] = React.useState<string | null>(null);

  // Values
  const [search, setSearch] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const [editValue, setEditValue] = React.useState<EnumGroupValue | null>(null);
  const [valueForm, setValueForm] = React.useState<ValueFormState>(emptyValueForm());
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetchEnumGroup(token, groupId)
      .then((g) => {
        setGroup(g);
        setNameValue(g.name);
        setDescValue(g.description ?? "");
      })
      .catch((err) => setError(err?.message || t("pro.system.enums.error.loadDetail")))
      .finally(() => setLoading(false));
  }, [token, groupId, t]);

  React.useEffect(() => { load(); }, [load]);

  const saveField = async (field: "name" | "description", value: string) => {
    if (!token || !group) return;
    setSavingMeta(true);
    setMetaError(null);
    try {
      const updated = await updateEnumGroup(token, groupId, { [field]: value || null });
      setGroup(updated);
      if (field === "name") { setNameValue(updated.name); setEditingName(false); }
      else { setDescValue(updated.description ?? ""); setEditingDesc(false); }
    } catch (err: any) {
      setMetaError(err?.message || t("pro.system.enums.error.saveMeta"));
    } finally {
      setSavingMeta(false);
    }
  };

  const openAdd = () => { setValueForm(emptyValueForm()); setFormError(null); setAddOpen(true); };
  const openEdit = (v: EnumGroupValue) => {
    setValueForm({
      key: v.key,
      label: v.label,
      description: v.description ?? "",
      meta: v.meta ? JSON.stringify(v.meta, null, 2) : "",
    });
    setFormError(null);
    setEditValue(v);
  };
  const closeModals = () => { setAddOpen(false); setEditValue(null); };

  const handleSubmitValue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setFormError(null);
    try {
      let parsedMeta: Record<string, unknown> | null = null;
      if (valueForm.meta.trim()) {
        try { parsedMeta = JSON.parse(valueForm.meta); }
        catch { setFormError(t("pro.system.enums.error.metaJson")); setSubmitting(false); return; }
      }
      const payload = {
        key: valueForm.key.trim(),
        label: valueForm.label.trim(),
        description: valueForm.description.trim() || null,
        meta: parsedMeta,
      };
      if (editValue) {
        await updateEnumGroupValue(token, groupId, editValue.id, {
          label: payload.label, description: payload.description, meta: payload.meta,
        });
      } else {
        await createEnumGroupValue(token, groupId, payload);
      }
      closeModals();
      load();
    } catch (err: any) {
      setFormError(err?.message || t("pro.system.enums.error.saveValue"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (v: EnumGroupValue) => {
    if (!token) return;
    const ok = await confirm(
      t("pro.system.enums.confirm.deleteTitle", { key: v.key }),
      t("pro.system.enums.confirm.deleteBody", { label: v.label }),
      t("shared.common.delete"), "danger",
    );
    if (!ok) return;
    try {
      await deleteEnumGroupValue(token, groupId, v.id);
      load();
    } catch (err: any) {
      setError(err?.message || t("pro.system.enums.error.delete"));
    }
  };

  const values = group?.values ?? [];
  const filtered = values.filter((v) =>
    !search || v.key.toLowerCase().includes(search.toLowerCase()) || v.label.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className={pageStyles.page}><div style={{ width: "100%", maxWidth: 960 }}>
      <Text level="body-medium" style={{ color: "#9CA3AF" }}>{t("shared.common.loading")}</Text>
    </div></div>
  );
  if (error || !group) return (
    <div className={pageStyles.page}><div style={{ width: "100%", maxWidth: 960 }}>
      <Callout variant="error">{error ?? t("pro.system.enums.error.notFound")}</Callout>
    </div></div>
  );

  const ValueModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
    <Modal open={open} setOpen={onClose} minWidth="440px" maxWidth="520px" closeOnClickOff>
      <div style={{ padding: "24px 28px" }}>
        <Text level="h2" weight="bold" style={{ marginBottom: 20 }}>
          {editValue ? t("pro.system.enums.valueModal.edit") : t("pro.system.enums.valueModal.add")}
        </Text>
        <form onSubmit={handleSubmitValue}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {editValue ? (
              <div>
                <span style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>{t("pro.system.enums.valueModal.key")}</span>
                <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 14, fontWeight: 600 }}>{editValue.key}</div>
              </div>
            ) : (
              <TextInput label={t("pro.system.enums.valueModal.keyRequired")} value={valueForm.key}
                onChange={(e) => setValueForm((f) => ({ ...f, key: e.target.value }))}
                placeholder={t("pro.system.enums.valueModal.keyPlaceholder")} disabled={submitting} autoFocus />
            )}
            <TextInput label={t("pro.system.enums.valueModal.labelRequired")} value={valueForm.label}
              onChange={(e) => setValueForm((f) => ({ ...f, label: e.target.value }))}
              placeholder={t("pro.system.enums.valueModal.labelPlaceholder")} disabled={submitting} />
            <TextInput label={t("pro.system.enums.valueModal.description")} value={valueForm.description}
              onChange={(e) => setValueForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t("pro.system.enums.valueModal.descriptionPlaceholder")} disabled={submitting} />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>{t("pro.system.enums.valueModal.metaJson")}</label>
              <textarea value={valueForm.meta}
                onChange={(e) => setValueForm((f) => ({ ...f, meta: e.target.value }))}
                disabled={submitting} rows={3}
                placeholder={t("pro.system.enums.valueModal.metaPlaceholder")}
                style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB",
                  fontSize: 13, fontFamily: "monospace", background: "var(--color-inputBg, #fff)",
                  color: "var(--color-fg, #111)", resize: "vertical" }} />
            </div>
          </div>
          {formError && <div style={{ marginTop: 14 }}><Callout variant="error">{formError}</Callout></div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            <Button type="button" color="neutral" onClick={onClose} disabled={submitting}>{t("shared.common.cancel")}</Button>
            <Button type="submit" color="primary"
              disabled={submitting || !valueForm.label.trim() || (!editValue && !valueForm.key.trim())}>
              {submitting ? t("pro.system.enums.saving") : t("shared.common.save")}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );

  return (
    <div className={pageStyles.page}>
      <div style={{ width: "100%", maxWidth: 960 }}>
        <button type="button"
          onClick={() => navigate({ to: "/system/data-catalog/enum-groups" })}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "#FFCF51",
            background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 16 }}>
          <ArrowLeft size={14} /> {t("pro.system.enums.title")}
        </button>

        <Card maxWidth={960} style={{ width: "100%", marginBottom: 20 }}>
          <div style={{ padding: "20px 24px" }}>
            {metaError && <div style={{ marginBottom: 12 }}><Callout variant="error">{metaError}</Callout></div>}

            {/* Name */}
            <div style={{ marginBottom: 12 }}>
              {editingName ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <TextInput value={nameValue} onChange={(e) => setNameValue(e.target.value)}
                    disabled={savingMeta} style={{ flex: 1 }} />
                  <Button type="button" color="primary" variant="solid" iconLeft={<Save size={14} />}
                    loading={savingMeta} onClick={() => saveField("name", nameValue)}
                    disabled={!nameValue.trim()}>{t("shared.common.save")}</Button>
                  <Button type="button" color="neutral" onClick={() => { setEditingName(false); setNameValue(group.name); }}>
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Text level="h1" weight="bold">{group.name}</Text>
                  {!group.isActive && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 9999,
                      background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB" }}>{t("pro.system.enums.badge.inactive")}</span>
                  )}
                  <button type="button" onClick={() => setEditingName(true)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 2 }}
                    title={t("shared.common.edit")}>
                    <Pencil size={13} />
                  </button>
                </div>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              {editingDesc ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <TextInput value={descValue} onChange={(e) => setDescValue(e.target.value)}
                    disabled={savingMeta} placeholder={t("pro.system.enums.form.descriptionPlaceholder")} style={{ flex: 1 }} />
                  <Button type="button" color="primary" variant="solid" iconLeft={<Save size={14} />}
                    loading={savingMeta} onClick={() => saveField("description", descValue)}>{t("shared.common.save")}</Button>
                  <Button type="button" color="neutral" onClick={() => { setEditingDesc(false); setDescValue(group.description ?? ""); }}>
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text level="body-medium" style={{ color: group.description ? "#6B7280" : "#D1D5DB" }}>
                    {group.description ?? t("pro.system.enums.noDescription")}
                  </Text>
                  <button type="button" onClick={() => setEditingDesc(true)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 2 }}
                    title={t("shared.common.edit")}>
                    <Pencil size={13} />
                  </button>
                </div>
              )}
            </div>

            <Text level="body-small" style={{ color: "#9CA3AF" }}>
              {t("pro.system.enums.codesTotal", { count: values.length })}
            </Text>
          </div>
        </Card>

        {/* Values table */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", pointerEvents: "none" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("pro.system.enums.filterPlaceholder")}
              style={{ width: "100%", paddingLeft: 32, paddingRight: 10, paddingTop: 7, paddingBottom: 7,
                borderRadius: 6, border: "1px solid #D1D5DB", fontSize: 13,
                background: "var(--color-inputBg, #fff)", color: "var(--color-fg, #111)", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {search && (
              <Text level="body-small" style={{ color: "#9CA3AF" }}>
                {t("pro.system.enums.filterCount", { filtered: filtered.length, total: values.length })}
              </Text>
            )}
            <Button type="button" color="primary" onClick={openAdd} iconLeft={<Plus size={14} />}>
              {t("pro.system.enums.addCode")}
            </Button>
          </div>
        </div>

        <Card maxWidth={960} style={{ width: "100%" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "24px 20px", textAlign: "center" }}>
              <Text level="body-medium" style={{ color: "#9CA3AF" }}>
                {search ? t("pro.system.enums.emptyFiltered") : t("pro.system.enums.emptyCodes")}
              </Text>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #E5E7EB" }}>
                    {[
                      t("pro.system.enums.col.key"),
                      t("pro.system.enums.col.label"),
                      t("pro.system.enums.col.severity"),
                      "",
                    ].map((h, i) => (
                      <th key={h || `actions-${i}`} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11,
                        fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em",
                        whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v, i) => (
                    <tr key={v.id} style={{ borderBottom: i < filtered.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                      <td style={{ padding: "10px 16px", fontFamily: "monospace", fontWeight: 600, fontSize: 12 }}>
                        {v.key}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        <div>{v.label}</div>
                        {v.description && (
                          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{v.description}</div>
                        )}
                      </td>
                      <td style={{ padding: "10px 16px" }}>{severityBadge((v.meta as Record<string, unknown> | null)?.severity as string | null)}</td>
                      <td style={{ padding: "10px 16px" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button type="button" onClick={() => openEdit(v)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 4, borderRadius: 4 }}
                            title={t("shared.common.edit")}>
                            <Pencil size={14} />
                          </button>
                          <button type="button" onClick={() => handleDelete(v)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444", padding: 4, borderRadius: 4 }}
                            title={t("shared.common.delete")}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <ValueModal open={addOpen} onClose={closeModals} />
      <ValueModal open={!!editValue} onClose={closeModals} />
      <ConfirmModal />
    </div>
  );
}
